import {
  defined,
  HeightReference,
  Entity,
  ArcType,
  PolygonHierarchy,
  CallbackProperty,
  Cartesian3,
  Cartesian2,
  BoundingRectangle,
  BillboardGraphics,
  CustomDataSource,
  BoundingSphere,
  Matrix4,
  HeadingPitchRange,
} from "cesium";
import {
  IOverlaysAddParams,
  IOverlaysDeleteParams,
  IResult,
  RESULT_STATUS,
  IOverlay,
  IPointSymbol,
  IPolylineSymbol,
  IPolygonSymbol,
  IPointGeometry,
  IPolylineGeometry,
  IPolygonGeometry,
  DefaultPoint,
  DefaultPolyline,
  DefaultPolygon,
  GEO_TYPE,
} from "@/types/cesiumMap";

import * as Utils from "../../../cesiumUtils";
import axios from "axios";
// import parseAPNG from "apng-js"; //apng开源库 https://github.com/davidmz/apng-js, npm版本v1.1.1

export default class Overlays {
  private static Instances = new Map<string, Overlays>();

  private viewer!: any;
  private entities: Entity.ConstructorOptions[]; //存储每次添加的结果

  private _imgTimerOfOverlays: Map<string, Map<string, any>>; //存储覆盖物的定时器，用于删除覆盖物时删除定时器

  // public markerClickEvent: any;
  // public markerMousemoveEvent: any;

  constructor(viewer: any) {
    this.viewer = viewer;
    this.entities = new Array<Entity.ConstructorOptions>();
    this._imgTimerOfOverlays = new Map<string, Map<string, any>>();
  }

  public static GetInstance(viewer: any): Overlays {
    const elementId = viewer.container;
    const instance = Overlays.Instances.get(elementId);
    if (instance instanceof Overlays) {
      return instance;
    } else {
      const newInstance = new Overlays(viewer);
      Overlays.Instances.set(elementId, newInstance);
      return newInstance;
    }
  }

  /**
   * addOverlays
   * 添加点、线、面覆盖物到地图上
   */
  public async addOverlays(params: IOverlaysAddParams): Promise<IResult> {
    //解析参数
    const type = params.type;
    const overlays = params.overlays;
    const symbol = params.symbol;
    const callback = params.callback;
    const viewToResults = params.viewToResults != false; //默认回显true

    const allPositions = new Array<Cartesian3>(); //存储所有覆盖物的所有点，用于回显所有覆盖物范围

    //清空上一次添加的结果
    this.entities = new Array<Entity.ConstructorOptions>();
    //取得对应type的图层
    let overlayLayer = this._getLayer(type);
    //逐个添加实体
    const that = this;
    //遍历每个overlay，逐个添加到地图上
    for (let i = 0; i < overlays.length; ++i) {
      const overlay = overlays[i];
      //解析单个覆盖物的参数
      const geotype_ = overlay.geotype;
      //每个覆盖物的属性数据
      const attributes_ = overlay.attributes;
      //基本参数
      let overlayEntity: any;
      const entityparams_ = {
        geotype: geotype_,
        geometry: overlay.geometry,
        id: overlay.id,
        type: overlay.type || type,
        symbol: overlay.symbol || symbol || that._getDefaultSymbol(geotype_),
        attributes: attributes_,
      };
      //覆盖物参数中的type与外部type不同时，使用覆盖物的type作为图层标识
      if (overlay.type && overlay.type != type) {
        overlayLayer = that._getLayer(overlay.type);
      }
      //根据不同类型添加覆盖物
      if (geotype_ == GEO_TYPE.POINT) {
        overlayEntity = await that._addPoint2Map(
          entityparams_,
          overlayLayer,
          allPositions
        );
      } else if (geotype_ == GEO_TYPE.POLYLINE) {
        overlayEntity = that._addPolyline2Map(
          entityparams_,
          overlayLayer,
          allPositions
        );
      } else if (geotype_ == GEO_TYPE.POLYGON) {
        overlayEntity = that._addPolygon2Map(
          entityparams_,
          overlayLayer,
          allPositions
        );
      } else {
        return {
          status: RESULT_STATUS.FAILED,
          message: "不支持的几何类型！ check the overlays 'geotype'",
        };
      }
      //存储加入地图的实体
      if (defined(overlayEntity)) {
        that.entities.push(overlayEntity);
      }
    }
    // overlays.forEach((overlay: IOverlay) => {});
    if (callback) {
      // callback(that.entities);
    }
    if (viewToResults) {
      //计算所有entity的包围球
      const bs = BoundingSphere.fromPoints(allPositions);
      //设置相机视角到包围球
      that.viewer.camera.viewBoundingSphere(
        bs,
        new HeadingPitchRange(0, -1.5, 2 * bs.radius)
      );
      that.viewer.camera.lookAtTransform(Matrix4.IDENTITY);
    }
    return {
      status: RESULT_STATUS.SUCCESS,
      message: "success add overlays",
      // data: that.entities,
    };
  }

  /**
   * deleteOverlays
   * 删除地图上指定的点、线、面覆盖物
   */
  public async deleteOverlays(params: IOverlaysDeleteParams): Promise<IResult> {
    const types = params.types || [];
    const ids = params.ids || [];
    //ids + types 均为空直接返回
    if (types.length == 0 && ids.length == 0) {
      return {
        status: RESULT_STATUS.SUCCESS,
        message: `0 overlays were deleted successfully`,
      };
    }
    const that = this;
    const both: boolean = ids.length > 0 && types.length > 0;
    let count = 0;
    //先拿到指定type图层，再删除指定id的entity
    const layers = new Array<any>();
    types.forEach((type: any) => {
      layers.push(that._getLayer(type));
    });
    //如果type为空，则删除所有图层中的指定id,如果没有找到指定图层，则直接结束
    if (types.length == 0) {
      for (let i = 0; i < that.viewer.dataSources.length; ++i) {
        layers.push(that.viewer.dataSources.get(i));
      }
    } else if (layers.length == 0) {
      return {
        status: RESULT_STATUS.SUCCESS,
        message: `${count} overlays were deleted successfully`,
      };
    }
    //逐个图层进行删除操作
    layers.forEach((dataSource: any) => {
      if (ids.length == 0) {
        count += dataSource.entities.values.length;
        dataSource.entities.removeAll();
        //删除定时器
        this._removeTimerOfImg(dataSource.name, undefined);
      }
      ids.forEach((id: any) => {
        if (dataSource.entities.removeById(id)) {
          ++count;
          //删除定时器
          this._removeTimerOfImg(dataSource.name, id);
        }
      });
    });

    return {
      status: RESULT_STATUS.SUCCESS,
      message: `${count} overlays were deleted successfully`,
    };
  }

  private async _addPoint2Map(
    params: IOverlay,
    dataSource: any,
    allPositions: Array<Cartesian3>
  ): Promise<Entity.ConstructorOptions> {
    const geo = params.geometry as IPointGeometry;
    const symbol = params.symbol as IPointSymbol;
    //图标
    let billboard: any;
    // @notice 暂时不搞这个
    // let imageSubRegion_: any;
    // if (symbol.imgBgSize) {
    //   imageSubRegion_ = new BoundingRectangle(
    //     undefined,
    //     undefined,
    //     symbol.imgBgSize[0],
    //     symbol.imgBgSize[1]
    //   );
    // }
    console.log(symbol, "symbol对象");

    if (symbol.url) {
      billboard = new BillboardGraphics({
        // image: symbol.url,
        pixelOffset: new Cartesian2(symbol.xoffset, symbol.yoffset),
        show: true,
        // eyeOffset:,
        horizontalOrigin: Utils.getHAnchorFromString(symbol.hAnchor),
        verticalOrigin: Utils.getVAnchorFromString(symbol.vAnchor),
        rotation: symbol.rotation,
        width: symbol.width,
        height: symbol.height,
        // imageSubRegion: imageSubRegion_,
        // disableDepthTestDistance: Number.POSITIVE_INFINITY,
      });
      //处理图标,是动图还是静图
      billboard.image = await this._loadImg(symbol.url, {
        interval: symbol.animatedPicInterval,
        amount: symbol.animatedPicNums,
        type: params.type,
        id: params.id,
      });
    }
    //图元
    let point: any;
    if (symbol.size && symbol.size > 0) {
      point = {
        color: Utils.getColorFromArray(symbol?.color),
        pixelSize: symbol?.size,
        outlineColor: Utils.getColorFromArray(symbol?.outline?.color),
        outlineWidth: symbol.outline?.width,
        disableDepthTestDistance: Number.POSITIVE_INFINITY, //深度检测，用于遮挡和透视
        // heightReference: HeightReference.CLAMP_TO_GROUND,//贴合地面
      };
    }
    //点位加入allPositions中
    allPositions.push(Cartesian3.fromDegrees(geo.x, geo.y, geo.z));
    // 添加到地图上并返回
    return dataSource.entities.add({
      id: params.id,
      name: params.type,
      position: Cartesian3.fromDegrees(geo.x, geo.y, geo.z),
      show: true,
      point: point,
      billboard: billboard,
      attributes: params.attributes, //直接默认为entity的一个property
    });
  }

  private _addPolyline2Map(
    params: IOverlay,
    dataSource: any,
    allPositions: Array<Cartesian3>
  ): Entity.ConstructorOptions {
    const geo = params.geometry as IPolylineGeometry;
    const positions = new Array<number>();
    geo.paths.forEach((element) => {
      positions.push(element.x);
      positions.push(element.y);
      //@notice 暂时不处理三维点的高度,点位均贴合地球表面
    });
    const symbol = params.symbol as IPolylineSymbol;
    //点位加入allPositions中
    const positions__ = Cartesian3.fromDegreesArray(positions);
    positions__.forEach((v: any) => {
      allPositions.push(v);
    });
    // 添加到地图上并返回
    return dataSource.entities.add({
      id: params.id,
      name: params.type,
      polyline: {
        // arcType: ArcType.NONE,
        positions: positions__,
        width: symbol.width || 1,
        material: Utils.getColorFromArray(symbol.color),
        distanceDisplayCondition: Number.POSITIVE_INFINITY, //显示折线的相机距离
        depthFailMaterial: Utils.getColorFromArray(symbol.color), //地下部分的线颜色
        clampToGround: true, //贴合地面之上，防止被底图遮挡
        zIndex: 1,
      },
      attributes: params.attributes, //直接默认为entity的一个property
    });
  }

  private _addPolygon2Map(
    params: IOverlay,
    dataSource: any,
    allPositions: Array<Cartesian3>
  ): Entity.ConstructorOptions {
    const geo = params.geometry as IPolygonGeometry;
    //@notice 暂时不处理holes
    const positions = new Array<number>();
    geo.rings.forEach((element) => {
      positions.push(element.x);
      positions.push(element.y);
      //@notice 暂时不处理三维点的高度,点位均贴合地球表面
    });
    const symbol = params.symbol as IPolygonSymbol;
    const hasOutline = symbol.outline != undefined;
    //点位加入allPositions中
    const positions__ = Cartesian3.fromDegreesArray(positions);
    positions__.forEach((v: any) => {
      allPositions.push(v);
    });
    // 添加到地图上并返回
    return dataSource.entities.add({
      id: params.id,
      name: params.type,
      polygon: {
        height: geo.height || 5, //多边形高度默认5
        outline: hasOutline,
        outlineColor: Utils.getColorFromArray(symbol.outline?.color),
        outlineWidth: symbol.outline?.width || 1, // windowsOS上不会起作用，默认是1 @link https://github.com/CesiumGS/cesium/issues/40
        material: Utils.getColorFromArray(symbol.color),
        // arcType: ArcType.NONE,
        hierarchy: new PolygonHierarchy(positions__),
        // distanceDisplayCondition: Number.POSITIVE_INFINITY, //显示折线的相机距离
        // zIndex: 10,
      },
      attributes: params.attributes, //直接默认为entity的一个property
    });
  }

  private _getDefaultSymbol(type: string): any {
    let symbol: any = DefaultPoint;
    switch (type) {
      case GEO_TYPE.POINT:
        symbol = DefaultPoint;
        break;
      case GEO_TYPE.POLYLINE:
        symbol = DefaultPolyline;
        break;
      case GEO_TYPE.POLYGON:
        symbol = DefaultPolygon;
        break;
      default:
        break;
    }
    return symbol;
  }

  private _getLayer(type: string | undefined): any {
    const overlayLayer_array = this.viewer.dataSources.getByName(
      type || "default-overlay-layer"
    );
    let overlayLayer: any;
    if (!overlayLayer_array || overlayLayer_array.length == 0) {
      overlayLayer = new CustomDataSource(type || "default-overlay-layer");
      this.viewer.dataSources.add(overlayLayer);
    } else {
      //虽然dataSources中允许同名，但是这里不会出现同名的图层,因此取第一个结果
      overlayLayer = overlayLayer_array[0];
    }
    return overlayLayer;
  }

  //给billboard设置image
  private async _loadImg(url: any, params: any): Promise<any> {
    if (url == undefined || url == "") {
      return;
    }
    if (params.amount == undefined) {
      return url;
    }
    //获取所有图片地址
    const amount = Number(params.amount);
    const urls = new Array<string>();
    for (let i = 0; i < amount; ++i) {
      urls.push(url + "/" + i.toString() + ".png");
    }
    //预缓存图片
    const res_urls = await this._load_png_urls(urls);
    //设置定时器
    let i = 0;
    const timer = self.setInterval(() => {
      ++i;
      i = i % res_urls.length;
    }, params.interval || 100);
    //保存定时器以便后续删除
    this._setTimerOfImg(timer, params);
    return new CallbackProperty(function () {
      return res_urls[i];
    }, false);
  }

  //设置定时器
  private _setTimerOfImg(timer: any, params: any) {
    const type_ = params.type || "default-overlay-layer";
    const id_ = params.id;
    let type_timer = this._imgTimerOfOverlays.get(type_);
    if (!type_timer) {
      type_timer = new Map<string, any>();
    }
    type_timer.set(id_, timer);
    this._imgTimerOfOverlays.set(type_, type_timer);
  }

  //删除定时器
  private _removeTimerOfImg(type: string, id: string | undefined): void {
    const type_timer_map = this._imgTimerOfOverlays.get(type);
    if (!type_timer_map) {
      return;
    }
    if (!id) {
      type_timer_map.forEach((timer: any) => {
        self.clearInterval(timer);
      });
      type_timer_map.clear();
      this._imgTimerOfOverlays.delete(type);
      return;
    }
    self.clearInterval(type_timer_map.get(id));
    type_timer_map.delete(id);
  }

  //预先缓存图片资源，没成功的就踢出去
  private async _load_png_urls(urls: Array<string>): Promise<Array<string>> {
    const resultUrl = new Array<string>();
    const promises = new Array<Promise<any>>();
    urls.forEach((url: string) => {
      promises.push(
        new Promise<string>((resolve, reject) => {
          axios
            .get(url, { responseType: "arraybuffer" })
            .then((response: any) => {
              resultUrl.push(url);
              resolve(url);
            })
            .catch((err) => {
              console.log(err.message, url);
              reject();
            });
        })
      );
    });
    await Promise.allSettled(promises);
    return resultUrl;
  }
}
